import React, { useState, useEffect } from "react";
import logo from "../../assets/logo.webp";
import AnchorLink from "react-anchor-link-smooth-scroll";
import { CiMenuFries } from "react-icons/ci";
import { IoMdClose } from "react-icons/io";
import { GrInstagram } from "react-icons/gr";

const Navbar = () => {
  const [menu, setMenu] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navItems = ["home", "about", "services", "work", "gallery", "contact"];

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClick = (item) => {
    setMenu(item);
    setIsMenuOpen(false);
  };

  const ConnectButton = () => (
    <AnchorLink
      href="#contact"
      offset={50}
      className="font-display bg-gradient-to-r from-[#df8908] via-[#e98a2f] to-[#f6b14d]
      text-white text-[16px] px-[18px] h-[44px] rounded-full
      hover:scale-105 transition-transform duration-500 border border-white/10
      inline-flex items-center justify-center leading-none no-underline overflow-hidden"
    >
      Connect With Me
    </AnchorLink>
  );

  return (
    <nav className="w-full px-4 md:px-6 lg:px-8 py-3 max-w-full overflow-visible sticky top-0 z-50 bg-[#161513]/70 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-[1200px] mx-auto flex items-center justify-between">
        {/* Logo */}
        <img
          src={logo}
          alt="Logo"
          className="w-[64px] h-[64px] sm:w-[76px] sm:h-[76px] md:w-[88px] md:h-[88px] 
          rounded-full object-cover border border-white/10"
        />

        {/* Desktop Menu */}
        <ul className="hidden md:flex items-center space-x-8 font-display">
          {navItems.map(
            (item) => (
              <li key={item} className="relative">
                <AnchorLink
                  href={`#${item}`}
                  offset={item !== "home" ? 50 : 0}
                  className="text-white no-underline hover:text-gray-300 text-lg"
                  onClick={() => setMenu(item)}
                >
                  <div className="flex flex-col items-center">
                    <span>
                      {item === "about"
                        ? "About Me"
                        : item.charAt(0).toUpperCase() + item.slice(1)}
                    </span>
                    <span
                      className={`mt-2 h-[3px] w-8 rounded-full transition-all duration-300
                      ${
                        menu === item
                          ? "bg-[#df8908] opacity-100"
                          : "bg-white/0 opacity-0"
                      }`}
                    />
                  </div>
                </AnchorLink>
              </li>
            )
          )}
        </ul>

        {/* Right Side Items Container */}
        <div className="flex items-center gap-3 md:gap-4 h-[44px]">
          {/* Connect Button - visible only on desktop */}
          <div className="hidden md:block">
            <ConnectButton />
          </div>

          {/* Instagram Icon */}
          <div className="hidden md:flex items-center z-50 h-[44px]">
            <a
              href="https://www.instagram.com/the.solitary.photographer/"
              target="_blank"
              rel="noreferrer"
              className="h-[44px] w-[44px] inline-flex items-center justify-center rounded-full
              bg-white/5 border border-white/10 text-[22px] sm:text-[22px] md:text-[22px]
              text-white/90 no-underline hover:text-white hover:bg-white/10 transition-colors
              leading-none"
              onClick={(e) => e.stopPropagation()}
            >
              <GrInstagram />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            type="button"
            aria-label="Toggle mobile menu"
            className="md:hidden text-2xl z-[60] h-[44px] w-[44px] inline-flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white"
          >
            {isMenuOpen ? <IoMdClose /> : <CiMenuFries />}
          </button>
        </div>

        {/* Overlay */}
        {isMenuOpen && (
          <div
            className="fixed inset-0 bg-black/55 z-40 md:hidden"
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="fixed top-[88px] left-3 right-3 md:hidden z-50 rounded-2xl border border-white/10 bg-[#1b1722]/95 backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.35)] overflow-hidden">
            <ul className="flex flex-col items-center py-4">
              {navItems.map((item) => (
                <li key={item} className="relative w-full text-center">
                  <AnchorLink
                    href={`#${item}`}
                    offset={item !== "home" ? 50 : 0}
                    className="font-display text-white/95 no-underline hover:text-white text-[22px] block py-3"
                    onClick={() => handleMenuClick(item)}
                  >
                    <div className="flex flex-col items-center">
                      <span>
                        {item === "about"
                          ? "About Me"
                          : item.charAt(0).toUpperCase() + item.slice(1)}
                      </span>
                      <span
                        className={`mt-2 h-[3px] w-10 rounded-full transition-all duration-300
                        ${
                          menu === item
                            ? "bg-[#df8908] opacity-100"
                            : "bg-white/0 opacity-0"
                        }`}
                      />
                    </div>
                  </AnchorLink>
                </li>
              ))}
            </ul>
            <div className="border-t border-white/10 px-4 py-3 flex justify-center">
              <a
                href="https://www.instagram.com/the.solitary.photographer/"
                target="_blank"
                rel="noreferrer"
                className="h-[40px] w-[40px] inline-flex items-center justify-center rounded-full
                bg-white/5 border border-white/10 text-[20px] text-white/90 no-underline hover:text-white hover:bg-white/10 transition-colors"
              >
                <GrInstagram />
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
